/**
 * CourseDetailPage — v1 MUI port of the standalone mockup.
 *
 * Mockup destination: instructions/education/CourseDetailPage.jsx (Tailwind).
 * Per the demo arc: shows MKTG 552's rich offering history + reviews when
 * navigated to via /courses/MKTG%20552; falls back to the slim catalog
 * basics for any other course code. Real per-course aggregations + reviews
 * land when the academic data pipeline does.
 *
 * Routes: /courses/:code
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SAGE, CREAM, WARM } from '../theme';
import COURSES from '../data/foster_courses.json';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

const BLUE = { main: '#6a7fc4', bg: '#e8edf5' };
const GOOD = '#3d7a4a';

// ============================================================================
// Rich mock data — kept inline for the demo course (MKTG 552)
// ============================================================================
const RICH_DATA = {
  'MKTG 552': {
    avgRating: 4.7,
    totalReviews: 47,
    ratingBreakdown: { 5: 32, 4: 11, 3: 3, 2: 1, 1: 0 },
    recommendRate: 96,
    workloadPattern: 'medium',
    theoryAvg: 3,
    appliedAvg: 3,
    commonResumeSkills: [
      { skill: 'Brand Management', count: 38 },
      { skill: 'Brand Strategy', count: 34 },
      { skill: 'Marketing Strategy', count: 29 },
      { skill: 'Consumer Insights', count: 22 },
      { skill: 'Marketing Analytics', count: 15 },
      { skill: 'Positioning', count: 12 },
    ],
    commonProjects: [
      'Brand audit for a CPG client',
      'Positioning strategy for a startup',
      'Brand extension proposal',
    ],
    reviewThemes: [
      { theme: 'Outstanding instruction', mentions: 41, sentiment: 'positive' },
      { theme: 'Engaging case discussions', mentions: 35, sentiment: 'positive' },
      { theme: 'Practical group project', mentions: 28, sentiment: 'positive' },
      { theme: 'Reading load can be heavy', mentions: 11, sentiment: 'neutral' },
    ],
    whoTakesIt: [
      { segment: 'Marketing concentrators', pct: 78 },
      { segment: 'Career pivoters into marketing', pct: 14 },
      { segment: 'Strategy / consulting bound', pct: 8 },
    ],
    pastOfferings: [
      {
        term: 'Winter 2025', instructor: 'Mark Forehand', enrollment: 68, avgRating: 4.8,
        resumeSkills: ['Brand Strategy', 'Brand Management', 'Marketing Strategy', 'Consumer Insights'],
        reviews: [
          { reviewer: "MBA '25", text: 'Mark is a phenomenal lecturer — makes brand frameworks click. The Pop-Tarts case alone is worth the tuition.' },
          { reviewer: "MBA '25", text: 'Group project with a real client was the highlight. We worked on positioning for a regional craft brewery and our recommendation actually got implemented.' },
          { reviewer: "MBA '25", text: 'Heavy reading load but every case is worth it. The Tropicana rebrand discussion changed how I think about brand equity.' },
        ],
      },
      {
        term: 'Winter 2024', instructor: 'Mark Forehand', enrollment: 72, avgRating: 4.7,
        resumeSkills: ['Brand Management', 'Brand Strategy', 'Positioning', 'Marketing Strategy'],
        reviews: [
          { reviewer: "MBA '24", text: "Best marketing class I've taken. Mark brings 30 years of brand consulting experience and it shows in every case discussion." },
          { reviewer: "MBA '24", text: "If you're switching into marketing, this is non-negotiable." },
        ],
      },
      {
        term: 'Winter 2023', instructor: 'Jennifer Aaker', enrollment: 58, avgRating: 4.5,
        resumeSkills: ['Brand Strategy', 'Marketing Research', 'Consumer Insights'],
        reviews: [
          { reviewer: "MBA '23", text: 'Jennifer brought a more research-heavy lens — slightly different feel than Mark but excellent content.' },
        ],
      },
    ],
  },
};

// ============================================================================
// Primitives
// ============================================================================

const Stars = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
      {Array.from({ length: 5 }).map((_, i) =>
        i < full ? <StarIcon key={i} sx={{ fontSize: 16, color: WARM.accent }} />
        : i === full && half ? <StarHalfIcon key={i} sx={{ fontSize: 16, color: WARM.accent }} />
        : <StarBorderIcon key={i} sx={{ fontSize: 16, color: SAGE[300] }} />
      )}
    </Box>
  );
};

const DotMeter = ({ level, color, max = 3 }) => (
  <Box sx={{ display: 'inline-flex', gap: 0.4 }}>
    {Array.from({ length: max }).map((_, i) => (
      <Box
        key={i}
        sx={{
          width: 8, height: 8, borderRadius: '50%',
          bgcolor: i < level ? color : SAGE[200],
        }}
      />
    ))}
  </Box>
);

const StatusPill = ({ status }) => {
  const map = {
    not_in_plan: { label: 'Not in plan', bg: CREAM[200], color: SAGE[600] },
    considering: { label: 'Considering', bg: WARM.accent + '30', color: WARM.accent },
    selected:    { label: 'In your plan', bg: SAGE[700], color: CREAM[50] },
    enrolled:    { label: 'Currently taking', bg: BLUE.bg, color: BLUE.main },
    completed:   { label: 'Completed', bg: '#e4f2e7', color: GOOD },
  };
  const s = map[status] || map.not_in_plan;
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        px: 1.5, py: 0.5, borderRadius: 99,
        bgcolor: s.bg, color: s.color,
        fontFamily: F, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5,
        textTransform: 'uppercase',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.color }} />
      {s.label}
    </Box>
  );
};

// Margot's three current courses are "enrolled"; a small set known to be in
// her plan are "selected"; everything else defaults to not_in_plan.
const MARGOT_ENROLLED = new Set(['B ECON 501 C', 'I S 500', 'FIN 557']);
const MARGOT_SELECTED = new Set(['MKTG 552', 'MKTG 568']);
const computeStatus = (code) => {
  if (MARGOT_ENROLLED.has(code)) return 'enrolled';
  if (MARGOT_SELECTED.has(code)) return 'selected';
  return 'not_in_plan';
};

// ============================================================================
// Page
// ============================================================================
const CourseDetailPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const decoded = decodeURIComponent(code || '');
  const course = COURSES.find((c) => c.code === decoded);
  const rich = RICH_DATA[decoded];
  const status = computeStatus(decoded);
  const [tab, setTab] = useState('overview');

  if (!course) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress sx={{ color: SAGE[500] }} size={28} />
        <Typography sx={{ fontFamily: F, mt: 2, color: SAGE[500] }}>
          Loading {decoded}…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: CREAM[100], minHeight: '100vh', pb: 6 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <Button
            onClick={() => navigate('/courses')}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{
              color: SAGE[600], textTransform: 'none', fontFamily: F,
              fontSize: '0.78rem', fontWeight: 500, p: 0,
              '&:hover': { bgcolor: 'transparent', color: SAGE[800] },
            }}
          >
            Course catalog
          </Button>
          <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[400], mx: 0.5 }}>›</Typography>
          <Typography sx={{ fontFamily: F, fontSize: '0.78rem', fontWeight: 600, color: SAGE[700] }}>
            {course.code}
          </Typography>
        </Box>

        {/* Hero card */}
        <Box
          sx={{
            bgcolor: CREAM[50], borderRadius: 3, border: `2px solid ${SAGE[200]}`,
            px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500] }}>
              {course.code}
            </Typography>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: SAGE[300] }} />
            <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[600], fontWeight: 500 }}>
              {course.department}
            </Typography>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: SAGE[300] }} />
            <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[600], fontWeight: 500 }}>
              {course.credits} credits
            </Typography>
            {course.quartersOffered && course.quartersOffered.length > 0 && (
              <>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: SAGE[300] }} />
                <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[600], fontWeight: 500 }}>
                  Offered: {course.quartersOffered.join(', ')}
                </Typography>
              </>
            )}
          </Box>
          <Typography sx={{ fontFamily: SERIF, fontSize: { xs: '1.6rem', md: '2.1rem' }, fontWeight: 600, color: SAGE[800], lineHeight: 1.15, mb: 1.5 }}>
            {course.title}
          </Typography>
          <Typography sx={{ fontFamily: SERIF, fontSize: '0.95rem', color: SAGE[600], lineHeight: 1.55, mb: 2.5 }}>
            {course.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <StatusPill status={status} />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              sx={{
                bgcolor: SAGE[700], color: CREAM[50], textTransform: 'none', fontFamily: F,
                fontSize: '0.85rem', fontWeight: 600, borderRadius: 2, px: 2.5, py: 1,
                '&:hover': { bgcolor: SAGE[800] },
              }}
              onClick={() => alert('Add to plan — coming soon (demo).')}
            >
              {status === 'enrolled' ? 'Log a project' : status === 'selected' ? 'Move to considering' : 'Add to my plan'}
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              fontFamily: F, fontSize: '0.85rem', fontWeight: 600,
              textTransform: 'none', color: SAGE[500],
            },
            '& .Mui-selected': { color: SAGE[800] + ' !important' },
            '& .MuiTabs-indicator': { backgroundColor: SAGE[700] },
          }}
        >
          <Tab value="overview" label="Overview" />
          <Tab
            value="past"
            label={`Past offerings${rich?.pastOfferings ? ` · ${rich.pastOfferings.length}` : ''}`}
            disabled={!rich?.pastOfferings}
          />
        </Tabs>

        {tab === 'overview' && (
          <OverviewTab course={course} rich={rich} />
        )}
        {tab === 'past' && rich?.pastOfferings && (
          <PastOfferingsTab offerings={rich.pastOfferings} />
        )}
      </Box>
    </Box>
  );
};

const SectionCard = ({ title, eyebrow, children, ...sx }) => (
  <Box
    sx={{
      bgcolor: CREAM[50], borderRadius: 2.5, border: `1.5px solid ${SAGE[200]}`,
      p: 2.5, ...sx,
    }}
  >
    {eyebrow && (
      <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500], mb: 1 }}>
        {eyebrow}
      </Typography>
    )}
    {title && (
      <Typography sx={{ fontFamily: F, fontSize: '0.95rem', fontWeight: 600, color: SAGE[800], mb: 1.5 }}>
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

const OverviewTab = ({ course, rich }) => {
  if (!rich) {
    return (
      <SectionCard eyebrow="Overview" title="More detailed signal coming soon">
        <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[500], lineHeight: 1.6 }}>
          We're rolling out aggregated reviews, instructor history, and resume-skill data course by course. {course.code} doesn't have the full panel yet — for now, the description above is the source of truth.
        </Typography>
        {course.reviews && course.reviews.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500], mb: 1 }}>
              From classmates
            </Typography>
            {course.reviews.map((r, i) => (
              <Typography key={i} sx={{ fontFamily: SERIF, fontSize: '0.88rem', fontStyle: 'italic', color: SAGE[600], lineHeight: 1.55, mb: 1 }}>
                "{r.text}"
                <Box component="span" sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[400], ml: 0.75, fontStyle: 'normal' }}>
                  — {r.reviewer}
                </Box>
              </Typography>
            ))}
          </Box>
        )}
      </SectionCard>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
      <SectionCard eyebrow="At a glance">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 600, color: SAGE[500], mb: 0.5, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Average rating
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: SERIF, fontSize: '1.6rem', fontWeight: 700, color: SAGE[800] }}>
                {rich.avgRating}
              </Typography>
              <Stars rating={rich.avgRating} />
              <Typography sx={{ fontFamily: F, fontSize: '0.7rem', color: SAGE[500] }}>
                {rich.totalReviews} reviews
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 600, color: SAGE[500], mb: 0.5, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Would recommend
            </Typography>
            <Typography sx={{ fontFamily: SERIF, fontSize: '1.6rem', fontWeight: 700, color: GOOD }}>
              {rich.recommendRate}%
              <Box component="span" sx={{ fontFamily: F, fontSize: '0.7rem', color: SAGE[500], fontWeight: 400, ml: 0.5 }}>
                of past students
              </Box>
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 600, color: SAGE[500], mb: 0.75, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Teaching style
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[700], fontWeight: 500 }}>Theory</Typography>
                <DotMeter level={rich.theoryAvg} color={BLUE.main} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[700], fontWeight: 500 }}>Applied</Typography>
                <DotMeter level={rich.appliedAvg} color={GOOD} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[700], fontWeight: 500 }}>Workload</Typography>
                <Typography sx={{ fontFamily: F, fontSize: '0.7rem', textTransform: 'capitalize', color: SAGE[600], fontWeight: 600 }}>
                  {rich.workloadPattern}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </SectionCard>

      <SectionCard eyebrow="What past students put on their resume">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          {rich.commonResumeSkills.map((s) => (
            <Chip
              key={s.skill}
              label={`${s.skill} · ${s.count}`}
              size="small"
              sx={{
                bgcolor: WARM.accent + '22', color: WARM.accent,
                fontFamily: F, fontSize: '0.72rem', fontWeight: 600, height: 24,
              }}
            />
          ))}
        </Box>
        <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500], mb: 1, mt: 2.5 }}>
          Common projects
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {rich.commonProjects.map((p) => (
            <Box key={p} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 14, color: SAGE[500], mt: 0.3 }} />
              <Typography sx={{ fontFamily: F, fontSize: '0.82rem', color: SAGE[700], lineHeight: 1.5 }}>
                {p}
              </Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>

      <SectionCard eyebrow="What classmates highlight">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rich.reviewThemes.map((t) => (
            <Box key={t.theme} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontFamily: F, fontSize: '0.82rem', color: SAGE[700], lineHeight: 1.4 }}>
                {t.theme}
              </Typography>
              <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[500], fontWeight: 600 }}>
                {t.mentions}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500], mb: 1, mt: 2.5 }}>
          Who takes it
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {rich.whoTakesIt.map((s) => (
            <Box key={s.segment} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[700] }}>
                {s.segment}
              </Typography>
              <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[600], fontWeight: 700 }}>
                {s.pct}%
              </Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>
    </Box>
  );
};

const PastOfferingsTab = ({ offerings }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {offerings.map((o, i) => (
      <SectionCard key={i}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography sx={{ fontFamily: SERIF, fontSize: '1.05rem', fontWeight: 600, color: SAGE[800] }}>
            {o.term}
          </Typography>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: SAGE[300] }} />
          <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[600], fontWeight: 500 }}>
            {o.instructor}
          </Typography>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: SAGE[300] }} />
          <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[500] }}>
            {o.enrollment} enrolled
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Stars rating={o.avgRating} />
            <Typography sx={{ fontFamily: F, fontSize: '0.85rem', fontWeight: 700, color: SAGE[700] }}>
              {o.avgRating}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
          {o.resumeSkills.map((s) => (
            <Chip
              key={s} label={s} size="small"
              sx={{ bgcolor: WARM.accent + '22', color: WARM.accent, fontFamily: F, fontSize: '0.7rem', fontWeight: 600, height: 22 }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 1.5, borderTop: `1px solid ${SAGE[100]}` }}>
          {o.reviews.map((r, j) => (
            <Box key={j}>
              <Typography sx={{ fontFamily: SERIF, fontSize: '0.88rem', fontStyle: 'italic', color: SAGE[700], lineHeight: 1.5 }}>
                "{r.text}"
              </Typography>
              <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[400], mt: 0.4 }}>
                — {r.reviewer}
              </Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>
    ))}
  </Box>
);

export default CourseDetailPage;
